import { CoreEnums } from '@/utils/domain/constants';
import { bindServices } from '@/services/api';

import {
  API_ROUTE,
  CUSTOMER_DISCOUNTS_API_ROUTE,
  DEFAULT_PAGE_SIZE,
  DETAIL_KIND,
  MODEL_NAME,
  TAB_KEYS,
} from './constants';
import {
  buildPassthroughParams,
  buildSortToken,
  normalizePage,
  unwrapPaged,
  validateHalfOpenUtcWindow,
} from './query';

const api = bindServices(MODEL_NAME);

const emptyListState = (sortField) => ({
  items: [],
  totalCount: 0,
  skip: 0,
  top: DEFAULT_PAGE_SIZE,
  sortField,
  sortDirection: 'desc',
  filters: {},
  error: null,
  loaded: false,
});

const moduleInitialState = {
  requestEpoch: 0,
  activeTab: TAB_KEYS.OVERVIEW,
  overview: {
    currencies: [],
    filters: {},
    error: null,
    loaded: false,
  },
  payments: emptyListState('effectivePaymentDate'),
  ledgers: emptyListState('effectiveSaleDate'),
  settlements: emptyListState('generatedAt'),
  beneficiary: {
    beneficiaryCompanyId: null,
    totals: [],
    ledgers: emptyListState('effectiveSaleDate'),
    settlements: emptyListState('generatedAt'),
    totalsFilters: {},
    error: null,
    loaded: false,
  },
  beneficiaryRequest: 0,
  detail: {
    kind: null,
    id: null,
    token: 0,
    item: null,
    error: null,
    discount: {
      loading: false,
      assignment: null,
      agreement: null,
      error: null,
    },
  },
};

const PAYMENT_ENUM_MAPS = {
  provider: CoreEnums.BillingPaymentProviderType,
  status: CoreEnums.BillingPaymentTransactionStatus,
};

const LEDGER_ENUM_MAPS = {
  beneficiaryActorKind: CoreEnums.ChannelAgreementBeneficiaryType,
  revenueType: CoreEnums.CommissionRevenueType,
  derivedState: CoreEnums.FinancialAdministrationLedgerState,
  paymentDisposition: CoreEnums.CommissionPaymentDisposition,
  ruleFrequency: CoreEnums.RuleFrequencyType,
};

const SETTLEMENT_ENUM_MAPS = {
  beneficiaryActorKind: CoreEnums.ChannelAgreementBeneficiaryType,
  status: CoreEnums.CommercialCommissionSettlementStatus,
};

const notifyError = function* (put, error) {
  if (!error?.message) return;
  yield put({
    type: 'global/showNotificationsForm',
    payload: { visible: true, type: 'error', message: error.message },
  });
};

const readEpoch = (state) => state[MODEL_NAME].requestEpoch || 0;

const acceptEpoch = (state, epoch) => epoch == null || epoch === state.requestEpoch;

function* isCurrentEpoch(select, epoch) {
  const current = yield select(readEpoch);
  return current === epoch;
}

const emptyDiscount = () => ({
  loading: false,
  assignment: null,
  agreement: null,
  error: null,
});

const buildListParams = (listState, enumMaps) => {
  const page = normalizePage({ skip: listState.skip, top: listState.top });
  if (!page.ok) return { ok: false, reason: 'page' };

  const filters = { ...(listState.filters || {}) };
  const datePairs = [
    ['from', 'to'],
    ['effectivePaymentFrom', 'effectivePaymentTo'],
    ['saleFrom', 'saleTo'],
    ['eligibilityFrom', 'eligibilityTo'],
    ['generatedFrom', 'generatedTo'],
    ['paidFrom', 'paidTo'],
  ];
  for (const [fromKey, toKey] of datePairs) {
    if (filters[fromKey] != null || filters[toKey] != null) {
      const check = validateHalfOpenUtcWindow(filters[fromKey], filters[toKey]);
      if (!check.ok) return { ok: false, reason: check.reason || 'dates' };
    }
  }

  const sort = buildSortToken(listState.sortField, listState.sortDirection);
  return {
    ok: true,
    params: buildPassthroughParams(
      {
        ...filters,
        skip: page.skip,
        top: page.top,
        ...(sort ? { sort } : {}),
      },
      { enumMaps },
    ),
  };
};

function* fetchPagedList({ listKey, enumMaps, route, payload }, { call, put, select }) {
  const epoch = yield select(readEpoch);
  if (
    payload?.filters != null ||
    payload?.skip != null ||
    payload?.top != null ||
    payload?.sortField
  ) {
    if (!(yield* isCurrentEpoch(select, epoch))) return;
    yield put({ type: 'patchList', payload: { listKey, epoch, ...payload } });
  }

  const listState = yield select((state) => state[MODEL_NAME][listKey]);
  const built = buildListParams(listState, enumMaps);
  if (!built.ok) {
    if (!(yield* isCurrentEpoch(select, epoch))) return;
    yield put({
      type: 'patchList',
      payload: {
        listKey,
        epoch,
        error: built.reason,
        items: [],
        totalCount: 0,
        loaded: false,
      },
    });
    return;
  }

  try {
    const res = yield call(api.getAll, {
      route,
      passthroughParams: built.params,
      readPriority: true,
    });
    if (!(yield* isCurrentEpoch(select, epoch))) return;
    const paged = unwrapPaged(res);
    yield put({
      type: 'patchList',
      payload: {
        listKey,
        epoch,
        items: paged.items,
        totalCount: paged.totalCount,
        error: null,
        loaded: true,
      },
    });
  } catch (e) {
    if (!(yield* isCurrentEpoch(select, epoch))) return;
    yield put({
      type: 'patchList',
      payload: {
        listKey,
        epoch,
        items: [],
        totalCount: 0,
        error: e?.message || 'error',
        loaded: false,
      },
    });
    yield* notifyError(put, e);
  }
}

const FinancialAdministrationModel = {
  namespace: MODEL_NAME,
  state: moduleInitialState,

  effects: {
    fetchOverview: [
      function* fetchOverview({ payload }, { call, put, select }) {
        const epoch = yield select(readEpoch);
        const filters = payload?.filters;
        const current = yield select((state) => state[MODEL_NAME].overview.filters);
        const nextFilters = filters != null ? filters : current;
        const check = validateHalfOpenUtcWindow(nextFilters.from, nextFilters.to);
        if (!check.ok) {
          if (!(yield* isCurrentEpoch(select, epoch))) return;
          yield put({
            type: 'setOverview',
            payload: {
              epoch,
              filters: nextFilters,
              error: check.reason,
              currencies: [],
              loaded: false,
            },
          });
          return;
        }

        if (!(yield* isCurrentEpoch(select, epoch))) return;
        yield put({ type: 'setOverview', payload: { epoch, filters: nextFilters, error: null } });
        try {
          const res = yield call(api.getAll, {
            route: `${API_ROUTE}/overview`,
            passthroughParams: buildPassthroughParams(nextFilters),
            readPriority: true,
          });
          if (!(yield* isCurrentEpoch(select, epoch))) return;
          yield put({
            type: 'setOverview',
            payload: {
              epoch,
              currencies: Array.isArray(res?.currencies) ? res.currencies : [],
              error: null,
              loaded: true,
            },
          });
        } catch (e) {
          if (!(yield* isCurrentEpoch(select, epoch))) return;
          yield put({
            type: 'setOverview',
            payload: { epoch, currencies: [], error: e?.message || 'error', loaded: false },
          });
          yield* notifyError(put, e);
        }
      },
      { type: 'takeLatest' },
    ],

    fetchPayments: [
      function* fetchPayments({ payload }, saga) {
        yield* fetchPagedList(
          {
            listKey: 'payments',
            enumMaps: PAYMENT_ENUM_MAPS,
            route: `${API_ROUTE}/payments`,
            payload,
          },
          saga,
        );
      },
      { type: 'takeLatest' },
    ],

    fetchLedgers: [
      function* fetchLedgers({ payload }, saga) {
        yield* fetchPagedList(
          {
            listKey: 'ledgers',
            enumMaps: LEDGER_ENUM_MAPS,
            route: `${API_ROUTE}/commission-ledgers`,
            payload,
          },
          saga,
        );
      },
      { type: 'takeLatest' },
    ],

    fetchSettlements: [
      function* fetchSettlements({ payload }, saga) {
        yield* fetchPagedList(
          {
            listKey: 'settlements',
            enumMaps: SETTLEMENT_ENUM_MAPS,
            route: `${API_ROUTE}/settlements`,
            payload,
          },
          saga,
        );
      },
      { type: 'takeLatest' },
    ],

    fetchBeneficiary: [
      function* fetchBeneficiary({ payload }, { call, put, select }) {
        const epoch = yield select(readEpoch);
        yield put({ type: 'bumpBeneficiaryRequest', payload: { epoch } });
        if (!(yield* isCurrentEpoch(select, epoch))) return;
        const requestId = yield select((state) => state[MODEL_NAME].beneficiaryRequest);
        const current = yield select((state) => state[MODEL_NAME].beneficiary);
        const beneficiaryCompanyId = payload?.beneficiaryCompanyId || current.beneficiaryCompanyId;
        if (!beneficiaryCompanyId) {
          if (!(yield* isCurrentEpoch(select, epoch))) return;
          yield put({
            type: 'setBeneficiary',
            payload: {
              epoch,
              requestId,
              error: 'missingBeneficiary',
              totals: [],
              beneficiaryCompanyId: null,
              loaded: false,
            },
          });
          return;
        }

        const totalsFilters =
          payload?.totalsFilters != null ? payload.totalsFilters : current.totalsFilters;
        const ledgerPatch = payload?.ledgers || {};
        const settlementPatch = payload?.settlements || {};
        const nextLedgers = {
          ...current.ledgers,
          ...ledgerPatch,
          filters: ledgerPatch.filters != null ? ledgerPatch.filters : current.ledgers.filters,
        };
        const nextSettlements = {
          ...current.settlements,
          ...settlementPatch,
          filters:
            settlementPatch.filters != null ? settlementPatch.filters : current.settlements.filters,
        };

        const totalsCheck = validateHalfOpenUtcWindow(totalsFilters.from, totalsFilters.to);
        if (!totalsCheck.ok) {
          if (!(yield* isCurrentEpoch(select, epoch))) return;
          yield put({
            type: 'setBeneficiary',
            payload: {
              epoch,
              requestId,
              beneficiaryCompanyId,
              totalsFilters,
              ledgers: nextLedgers,
              settlements: nextSettlements,
              totals: [],
              error: totalsCheck.reason,
              loaded: false,
            },
          });
          return;
        }

        const ledgerBuilt = buildListParams(nextLedgers, LEDGER_ENUM_MAPS);
        const settlementBuilt = buildListParams(nextSettlements, SETTLEMENT_ENUM_MAPS);
        if (!ledgerBuilt.ok || !settlementBuilt.ok) {
          if (!(yield* isCurrentEpoch(select, epoch))) return;
          yield put({
            type: 'setBeneficiary',
            payload: {
              epoch,
              requestId,
              beneficiaryCompanyId,
              totalsFilters,
              ledgers: { ...nextLedgers, error: ledgerBuilt.ok ? null : ledgerBuilt.reason },
              settlements: {
                ...nextSettlements,
                error: settlementBuilt.ok ? null : settlementBuilt.reason,
              },
              error: 'page',
              loaded: false,
            },
          });
          return;
        }

        if (!(yield* isCurrentEpoch(select, epoch))) return;
        yield put({
          type: 'setBeneficiary',
          payload: {
            epoch,
            requestId,
            beneficiaryCompanyId,
            totalsFilters,
            ledgers: { ...nextLedgers, error: null },
            settlements: { ...nextSettlements, error: null },
            error: null,
          },
        });

        try {
          const ledgerParams = ledgerBuilt.params;
          const settlementParams = settlementBuilt.params;
          const passthroughParams = buildPassthroughParams({
            ...totalsFilters,
            derivedState: ledgerParams.derivedState,
            saleFrom: ledgerParams.saleFrom,
            saleTo: ledgerParams.saleTo,
            eligibilityFrom: ledgerParams.eligibilityFrom,
            eligibilityTo: ledgerParams.eligibilityTo,
            ruleFrequency: ledgerParams.ruleFrequency,
            ledgerSkip: ledgerParams.skip,
            ledgerTop: ledgerParams.top,
            ledgerSort: ledgerParams.sort,
            generatedFrom: settlementParams.generatedFrom,
            generatedTo: settlementParams.generatedTo,
            paidFrom: settlementParams.paidFrom,
            paidTo: settlementParams.paidTo,
            settlementSkip: settlementParams.skip,
            settlementTop: settlementParams.top,
            settlementSort: settlementParams.sort,
            currency: totalsFilters.currency || ledgerParams.currency || settlementParams.currency,
          });

          const res = yield call(api.getById, {
            route: `${API_ROUTE}/beneficiaries`,
            id: beneficiaryCompanyId,
            passthroughParams,
            readPriority: true,
          });

          if (!(yield* isCurrentEpoch(select, epoch))) return;
          const ledgerPage = unwrapPaged(res?.ledgers);
          const settlementPage = unwrapPaged(res?.settlements);
          yield put({
            type: 'setBeneficiary',
            payload: {
              epoch,
              requestId,
              beneficiaryCompanyId: res?.beneficiaryCompanyId || beneficiaryCompanyId,
              totals: Array.isArray(res?.totals) ? res.totals : [],
              ledgers: {
                ...nextLedgers,
                items: ledgerPage.items,
                totalCount: ledgerPage.totalCount,
                error: null,
              },
              settlements: {
                ...nextSettlements,
                items: settlementPage.items,
                totalCount: settlementPage.totalCount,
                error: null,
              },
              error: null,
              loaded: true,
            },
          });
        } catch (e) {
          if (!(yield* isCurrentEpoch(select, epoch))) return;
          yield put({
            type: 'setBeneficiary',
            payload: {
              epoch,
              requestId,
              totals: [],
              ledgers: { ...nextLedgers, items: [], totalCount: 0 },
              settlements: { ...nextSettlements, items: [], totalCount: 0 },
              error: e?.message || 'error',
              loaded: false,
            },
          });
          yield* notifyError(put, e);
        }
      },
      { type: 'takeLatest' },
    ],

    openDetail: [
      function* openDetail({ payload }, { call, put, select }) {
        const kind = payload?.kind;
        const id = payload?.id;
        if (!kind || !id) return;

        const epoch = yield select(readEpoch);
        yield put({ type: 'beginDetail', payload: { epoch, kind, id } });
        const opened = yield select((state) => state[MODEL_NAME].detail);
        if (opened.kind !== kind || String(opened.id) !== String(id)) return;
        const token = opened.token;

        const routeSegment =
          kind === DETAIL_KIND.PAYMENT
            ? 'payments'
            : kind === DETAIL_KIND.LEDGER
            ? 'commission-ledgers'
            : 'settlements';

        try {
          const res = yield call(api.getById, {
            route: `${API_ROUTE}/${routeSegment}`,
            id,
            readPriority: true,
          });
          if (!(yield* isCurrentEpoch(select, epoch))) return;
          const current = yield select((state) => state[MODEL_NAME].detail);
          if (current.token !== token) return;
          const item = Array.isArray(res) ? res[0] : res;
          yield put({ type: 'setDetail', payload: { epoch, token, item, error: null } });

          if (kind === DETAIL_KIND.PAYMENT) {
            yield put({
              type: 'fetchPaymentDiscount',
              payload: { payment: item, token, epoch, paymentId: id },
            });
          }
        } catch (e) {
          if (!(yield* isCurrentEpoch(select, epoch))) return;
          const current = yield select((state) => state[MODEL_NAME].detail);
          if (current.token !== token) return;
          yield put({
            type: 'setDetail',
            payload: { epoch, token, item: null, error: e?.message || 'error' },
          });
          yield* notifyError(put, e);
        }
      },
      { type: 'takeLatest' },
    ],

    fetchPaymentDiscount: [
      function* fetchPaymentDiscount({ payload }, { call, put, select }) {
        const payment = payload?.payment;
        const token = payload?.token;
        const epoch = payload?.epoch;
        const paymentId = payload?.paymentId;
        const assignmentId = payment?.discountAssignmentId;
        const subscriptionId = payment?.companyBillingSubscriptionId;
        const discountGuard = { epoch, token, paymentId };

        const stillCurrent = function* () {
          if (!(yield* isCurrentEpoch(select, epoch))) return false;
          const detail = yield select((state) => state[MODEL_NAME].detail);
          return (
            detail.token === token &&
            detail.kind === DETAIL_KIND.PAYMENT &&
            String(detail.id) === String(paymentId)
          );
        };

        if (!(yield* stillCurrent())) return;

        if (!assignmentId) {
          yield put({
            type: 'setDetailDiscount',
            payload: { ...discountGuard, ...emptyDiscount() },
          });
          return;
        }

        if (!subscriptionId) {
          yield put({
            type: 'setDetailDiscount',
            payload: {
              ...discountGuard,
              loading: false,
              assignment: { id: assignmentId },
              agreement: null,
              error: 'missingSubscription',
            },
          });
          return;
        }

        yield put({
          type: 'setDetailDiscount',
          payload: {
            ...discountGuard,
            loading: true,
            assignment: null,
            agreement: null,
            error: null,
          },
        });

        try {
          const assignmentsRes = yield call(api.getAll, {
            route: `${CUSTOMER_DISCOUNTS_API_ROUTE}/subscriptions/${subscriptionId}/assignments`,
            readPriority: true,
          });
          if (!(yield* stillCurrent())) return;
          const assignments = unwrapPaged(assignmentsRes).items;
          const assignment =
            assignments.find((row) => String(row.id) === String(assignmentId)) || null;

          let agreement = null;
          if (assignment?.agreementId) {
            agreement = yield call(api.getById, {
              route: `${CUSTOMER_DISCOUNTS_API_ROUTE}/agreements`,
              id: assignment.agreementId,
              readPriority: true,
            });
            if (Array.isArray(agreement)) agreement = agreement[0];
          }

          if (!(yield* stillCurrent())) return;
          yield put({
            type: 'setDetailDiscount',
            payload: {
              ...discountGuard,
              loading: false,
              assignment: assignment || { id: assignmentId },
              agreement,
              error: assignment ? null : 'assignmentNotFound',
            },
          });
        } catch (e) {
          if (!(yield* stillCurrent())) return;
          yield put({
            type: 'setDetailDiscount',
            payload: {
              ...discountGuard,
              loading: false,
              assignment: { id: assignmentId },
              agreement: null,
              error: e?.message || 'error',
            },
          });
          yield* notifyError(put, e);
        }
      },
      { type: 'takeLatest' },
    ],

    *closeDetail(_, { put }) {
      yield put({ type: 'closeDetailState' });
    },

    *clearBeneficiary({ payload }, { put }) {
      yield put({ type: 'applyClearBeneficiary', payload });
    },

    *setActiveTab({ payload }, { put }) {
      yield put({ type: 'applyActiveTab', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    applyActiveTab(state, { payload }) {
      return { ...state, activeTab: payload || TAB_KEYS.OVERVIEW };
    },

    setOverview(state, { payload }) {
      const { epoch, ...rest } = payload || {};
      if (!acceptEpoch(state, epoch)) return state;
      return {
        ...state,
        overview: { ...state.overview, ...rest },
      };
    },

    patchList(state, { payload }) {
      const { listKey, epoch, ...rest } = payload || {};
      if (!acceptEpoch(state, epoch)) return state;
      if (!listKey || !state[listKey]) return state;
      return {
        ...state,
        [listKey]: { ...state[listKey], ...rest },
      };
    },

    bumpBeneficiaryRequest(state, { payload }) {
      if (!acceptEpoch(state, payload?.epoch)) return state;
      return {
        ...state,
        beneficiaryRequest: (state.beneficiaryRequest || 0) + 1,
      };
    },

    setBeneficiary(state, { payload }) {
      const { epoch, requestId, ...rest } = payload || {};
      if (!acceptEpoch(state, epoch)) return state;
      if (requestId != null && requestId !== state.beneficiaryRequest) return state;
      return {
        ...state,
        beneficiary: { ...state.beneficiary, ...rest },
      };
    },

    applyClearBeneficiary(state, { payload }) {
      return {
        ...state,
        beneficiaryRequest: (state.beneficiaryRequest || 0) + 1,
        beneficiary: {
          ...moduleInitialState.beneficiary,
          ...(payload || {}),
          loaded: false,
        },
      };
    },

    beginDetail(state, { payload }) {
      if (!acceptEpoch(state, payload?.epoch)) return state;
      return {
        ...state,
        detail: {
          kind: payload.kind,
          id: payload.id,
          token: (state.detail.token || 0) + 1,
          item: null,
          error: null,
          discount: emptyDiscount(),
        },
      };
    },

    setDetail(state, { payload }) {
      const { epoch, token, ...rest } = payload || {};
      if (!acceptEpoch(state, epoch)) return state;
      if (token != null && token !== state.detail.token) return state;
      return {
        ...state,
        detail: { ...state.detail, ...rest },
      };
    },

    setDetailDiscount(state, { payload }) {
      const { epoch, token, paymentId, ...discount } = payload || {};
      if (!acceptEpoch(state, epoch)) return state;
      if (token != null && token !== state.detail.token) return state;
      if (state.detail.kind !== DETAIL_KIND.PAYMENT) return state;
      if (paymentId != null && String(state.detail.id) !== String(paymentId)) return state;
      return {
        ...state,
        detail: {
          ...state.detail,
          discount: { ...state.detail.discount, ...discount },
        },
      };
    },

    closeDetailState(state) {
      return {
        ...state,
        detail: {
          ...moduleInitialState.detail,
          token: (state.detail.token || 0) + 1,
        },
      };
    },

    reset(state) {
      return {
        ...moduleInitialState,
        requestEpoch: (state?.requestEpoch || 0) + 1,
      };
    },
  },
};

export default FinancialAdministrationModel;
